import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  clearPromoActiveTemplate,
  deletePromoTemplate,
  getPromoErrorMessage,
  getPromoLivePreview,
  getPromoPayload,
  isPromoApiSuccess,
  listPromoSurfaces,
  setPromoActiveTemplate,
  setPromoSurfaceEnabled,
} from '../../services/promoSurfacesApi';
import LivePreviewPanel from './LivePreviewPanel';
import {
  TEMPLATE_STATUSES,
  activateResultMessage,
  canActivateTemplate,
  clearActiveMessage,
  describeBlockers,
  formatTimestamp,
  formatVersionWindow,
  getPromoSurface,
  ingestStatusLabel,
  parseLivePreview,
  parseNestedTemplates,
  parseSurfaceList,
  statusLabel,
  surfaceSwitchMessage,
  templateBlockers,
  templateIngestStatus,
  warningLabel,
} from './promoSurfacesUtils';
import { usePromoSurfacesSession } from './usePromoSurfacesSession';

const PAGE_SIZE = 20;

function statusColor(status) {
  if (status === 'active') return 'success';
  return 'default';
}

function PromoSurfaceList() {
  const { surfaceKey } = useParams();
  const surface = getPromoSurface(surfaceKey);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get('status') || '';
  const query = searchParams.get('q') || '';
  const offset = Math.max(0, Number(searchParams.get('offset') || 0) || 0);
  const [queryDraft, setQueryDraft] = useState(query);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [slot, setSlot] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [previewLoading, setPreviewLoading] = useState(true);
  const [appVersion, setAppVersion] = useState('');
  const [error, setError] = useState('');
  const [pending, setPending] = useState(null);
  const [working, setWorking] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { handleApiResponse } = usePromoSurfacesSession(setError);

  const updateParams = (patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === '' || value == null) next.delete(key);
      else next.set(key, String(value));
    });
    setSearchParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    if (!surface) return;
    setLoading(true);
    setError('');
    try {
      const listRes = await listPromoSurfaces({
        keys: surface.key,
        include: 'templates',
        q: query || undefined,
        status: status || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      if (handleApiResponse(listRes, 'Could not load templates.')) {
        setRows([]);
        setTotal(0);
        setSlot(null);
        return;
      }
      const match = parseSurfaceList(getPromoPayload(listRes)).surfaces.find((item) => item.key === surface.key);
      if (!match) {
        setError('Could not load this slot.');
        setRows([]);
        setTotal(0);
        setSlot(null);
        return;
      }
      const parsed = parseNestedTemplates(match);
      setSlot(match);
      setRows(parsed.templates);
      setTotal(parsed.total);
    } catch (loadError) {
      setError(loadError.message || 'Could not load templates.');
    } finally {
      setLoading(false);
    }
  }, [handleApiResponse, offset, query, status, surface]);

  const loadPreview = useCallback(async () => {
    setPreviewLoading(true);
    setPreviewError('');
    try {
      const previewRes = await getPromoLivePreview(appVersion);
      if (isPromoApiSuccess(previewRes)) {
        setPreview(parseLivePreview(getPromoPayload(previewRes)));
      } else {
        setPreview(null);
        setPreviewError(getPromoErrorMessage(previewRes, 'Could not load the live preview.'));
      }
    } catch (previewLoadError) {
      setPreviewError(previewLoadError.message || 'Could not load the live preview.');
    } finally {
      setPreviewLoading(false);
    }
  }, [appVersion]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  useEffect(() => {
    setQueryDraft(query);
  }, [query]);

  useEffect(() => {
    const notice = location.state?.notice;
    if (!notice) return;
    setSnackbar({ open: true, message: notice, severity: 'success' });
    navigate(location.pathname + location.search, { replace: true, state: null });
  }, [location.pathname, location.search, location.state, navigate]);

  const finishAction = async (message) => {
    setSnackbar({ open: true, message, severity: 'success' });
    setPending(null);
    await Promise.all([load(), loadPreview()]);
  };

  const handleToggle = async (enabled) => {
    if (!surface) return;
    setSwitching(true);
    setError('');
    try {
      const result = await setPromoSurfaceEnabled(surface.key, enabled);
      if (handleApiResponse(result, enabled ? 'Could not turn this surface on.' : 'Could not turn this surface off.')) {
        return;
      }
      await finishAction(surfaceSwitchMessage(getPromoPayload(result), surface.title));
    } catch (toggleError) {
      setError(toggleError.message || 'Could not update this surface.');
    } finally {
      setSwitching(false);
    }
  };

  const handleConfirm = async () => {
    if (!pending || !surface) return;
    setWorking(true);
    setError('');
    try {
      if (pending.type === 'activate') {
        const result = await setPromoActiveTemplate(surface.key, pending.template.id);
        if (handleApiResponse(result, 'Could not make this template active.')) return;
        await finishAction(activateResultMessage(getPromoPayload(result)));
      } else if (pending.type === 'delete') {
        const result = await deletePromoTemplate(surface.key, pending.template.id);
        if (handleApiResponse(result, 'Could not delete this template.')) return;
        await finishAction(`Deleted ${pending.template.name}.`);
      } else if (pending.type === 'clear') {
        const result = await clearPromoActiveTemplate(surface.key);
        if (handleApiResponse(result, 'Could not remove the active template.')) return;
        await finishAction(clearActiveMessage(surface.title));
      }
    } catch (actionError) {
      setError(actionError.message || 'The request failed.');
    } finally {
      setWorking(false);
    }
  };

  if (!surface) {
    return <Alert severity="error">Unknown slot. Open the promo surfaces page and pick a library.</Alert>;
  }

  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + rows.length, total);
  const warning = warningLabel(slot?.warning);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box>
          <Button component={Link} to="/admin/promo" size="small" sx={{ mb: 1 }}>
            All slots
          </Button>
          <Typography variant="h6">{surface.title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {surface.summary} {surface.creative} One template in this library can be active. The others are drafts.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <IconButton onClick={load} disabled={loading} title="Refresh">
            <RefreshIcon />
          </IconButton>
          <Button
            component={Link}
            to={`/admin/promo/${surface.key}/new`}
            variant="contained"
            startIcon={<AddIcon />}
          >
            Add template
          </Button>
        </Box>
      </Box>

      {slot && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(slot.enabled)}
                disabled={switching || working}
                onChange={(event) => handleToggle(event.target.checked)}
                inputProps={{ 'aria-label': `${surface.title} surface` }}
              />
            }
            label={slot.enabled ? 'On' : 'Off'}
          />
          <Chip size="small" color={slot.serving ? 'success' : 'default'} label={slot.serving ? 'Showing in the app' : 'Not showing'} />
          <Typography variant="body2" color="text.secondary">
            {slot.active_template ? `Active: ${slot.active_template.name}` : 'No active template'}
            {` · ${Number(slot.template_count) || 0} templates`}
          </Typography>
          {slot.active_template && (
            <Button size="small" disabled={working || switching} onClick={() => setPending({ type: 'clear' })}>
              Remove from slot
            </Button>
          )}
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {warning && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {warning}
        </Alert>
      )}

      <LivePreviewPanel
        preview={preview}
        loading={previewLoading}
        error={previewError}
        appVersion={appVersion}
        onApplyVersion={setAppVersion}
      />

      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          label="Search name or label"
          value={queryDraft}
          onChange={(event) => setQueryDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') updateParams({ q: queryDraft.trim(), offset: 0 });
          }}
        />
        <Button variant="outlined" onClick={() => updateParams({ q: queryDraft.trim(), offset: 0 })}>
          Search
        </Button>
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(event) => updateParams({ status: event.target.value, offset: 0 })}
          sx={{ minWidth: 160 }}
        >
          {TEMPLATE_STATUSES.map((option) => (
            <MenuItem key={option.value || 'all'} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {loading ? (
        <CircularProgress size={28} />
      ) : rows.length === 0 ? (
        <Alert severity="info">No templates in this library match that filter.</Alert>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Label</TableCell>
                <TableCell>Link</TableCell>
                <TableCell>App versions</TableCell>
                <TableCell>Video</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((template) => {
                const active = template.is_active === true || template.status === 'active';
                const blockers = templateBlockers(template);
                const reason = describeBlockers(blockers);
                const canActivate = canActivateTemplate(template);
                return (
                  <TableRow key={template.id} hover>
                    <TableCell>
                      {template.name}
                      {template.activate_on_ready && (
                        <Typography variant="caption" display="block">
                          Becomes active when the video is ready
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={statusLabel(template.status)} color={statusColor(template.status)} />
                      {reason && (
                        <Typography variant="caption" display="block" sx={{ maxWidth: 220 }}>
                          {reason}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{template.label || '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 180, wordBreak: 'break-all' }}>{template.link || '—'}</TableCell>
                    <TableCell>{formatVersionWindow(template.min_app_version, template.max_app_version)}</TableCell>
                    <TableCell>{ingestStatusLabel(templateIngestStatus(template))}</TableCell>
                    <TableCell>{formatTimestamp(template.updated_at)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <IconButton
                          component={Link}
                          to={`/admin/promo/${surface.key}/${template.id}`}
                          aria-label={`Edit ${template.name}`}
                          size="small"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        {!active && (
                          <Tooltip title={canActivate ? 'Make this the active template for this slot' : reason}>
                            <span>
                              <Button
                                size="small"
                                disabled={!canActivate}
                                onClick={() => setPending({ type: 'activate', template })}
                              >
                                Make active
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                        <Button
                          size="small"
                          color="error"
                          aria-label={`Delete ${template.name}`}
                          onClick={() => setPending({ type: 'delete', template })}
                        >
                          Delete
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {total === 0 ? '0 templates' : `${from}–${to} of ${total}`}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button disabled={offset <= 0} onClick={() => updateParams({ offset: Math.max(0, offset - PAGE_SIZE) })}>
            Previous
          </Button>
          <Button disabled={offset + PAGE_SIZE >= total} onClick={() => updateParams({ offset: offset + PAGE_SIZE })}>
            Next
          </Button>
        </Box>
      </Box>

      <Dialog open={Boolean(pending)} onClose={() => !working && setPending(null)}>
        <DialogTitle>
          {pending?.type === 'delete'
            ? 'Delete this template?'
            : pending?.type === 'clear'
              ? 'Remove the active template?'
              : 'Make this template active?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pending?.type === 'delete' &&
              `Delete “${pending.template.name}”? This cannot be undone. If it is the active template, this slot then has no active template.`}
            {pending?.type === 'clear' &&
              `The active template becomes a draft. The On/Off switch stays as it is.`}
            {pending?.type === 'activate' &&
              `“${pending.template.name}” becomes the active ${surface.title.toLowerCase()}. The previous active template on this slot becomes a draft. The On/Off switch stays as it is, and the other slots do not change.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)} disabled={working}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={pending?.type === 'delete' ? 'error' : 'primary'}
            onClick={handleConfirm}
            disabled={working}
          >
            {working
              ? 'Working…'
              : pending?.type === 'delete'
                ? 'Delete'
                : pending?.type === 'clear'
                  ? 'Remove'
                  : 'Make it active'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((current) => ({ ...current, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PromoSurfaceList;
